// SubscriptionContext.tsx
// Global subscription state management using React Context
// Provides subscription tier and access checks to all components

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Platform } from 'react-native';
import { SubscriptionTier } from '../models/PrivateProfile';
import { SubscriptionService, PurchasesPackage } from '../services/subscriptionService';
import { SubscriptionRepository } from '../repositories/subscriptionRepository';
import { auth, onAuthStateChanged } from '../config/firebase';
import { errorReporting } from '../services/errorReportingService';
import { loggingService } from '../services/loggingService';
import { 
  purchaseStarted,
  purchaseCompleted,
  purchaseFailed,
  purchaseCancelled,
  purchasesRestored,
  restorePurchasesFailed,
} from '../utils/logging/actions';

interface SubscriptionContextValue {
  // Subscription state
  tier: SubscriptionTier;
  expiresAt: number | null;
  isLoading: boolean;
  source: string | undefined;
  managementURL: string | null | undefined;
  
  // Access checks (convenience helpers)
  hasStandardAccess: boolean;
  hasAIAccess: boolean;
  
  // Actions
  refreshSubscription: () => Promise<void>;
  purchasePackage: (pkg: PurchasesPackage) => Promise<'success' | 'cancelled' | 'failed'>;
  restorePurchases: (trigger?: 'manual' | 'automatic') => Promise<boolean>;
  
  // Navigation helper
  showPaywall: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(undefined);

interface SubscriptionProviderProps {
  children: React.ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const [tier, setTier] = useState<SubscriptionTier>('free');
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [paywallCallback, setPaywallCallback] = useState<(() => void) | null>(null);
  const [source, setSource] = useState<string | undefined>(undefined);
  const [managementURL, setManagementURL] = useState<string | null | undefined>(undefined);

  /**
   * Internal restore purchases implementation (can be called from auth listener)
   */
  const restorePurchasesInternal = async (trigger: 'manual' | 'automatic' = 'manual'): Promise<boolean> => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error('User not authenticated');
      }

      const result = await SubscriptionService.restorePurchases();
      if (!result.success) {
        const errorMessage = result.error || 'Restore failed';
        
        // Log failed restoration
        await loggingService.log(purchasesRestored(false, trigger, {
          errorMessage,
        }));
        
        throw new Error(errorMessage);
      }

      // Update state
      if (result.data) {
        const subscriptionInfo = SubscriptionService.getSubscriptionInfo(result.data);
        const entitlements = Object.keys(result.data.entitlements.active);
        
        // Check if any active subscription was found
        const hasActiveSubscription = subscriptionInfo.tier !== 'free';
        
        if (!hasActiveSubscription) {
          console.log('[SubscriptionContext] No active purchases found to restore');
          
          // Log successful check but no purchases found
          const allEntitlements = Object.keys(result.data.entitlements.all);
          await loggingService.log(purchasesRestored(true, trigger, {
            tier: 'free',
            customerId: result.data.originalAppUserId,
            entitlements: allEntitlements, // Log all entitlements (even if inactive)
          }));
          
          return false; // Return false if no purchases found
        }

        setTier(subscriptionInfo.tier);
        setExpiresAt(subscriptionInfo.expiresAt);
        setSource(subscriptionInfo.source);
        setManagementURL(subscriptionInfo.managementURL);

        // Sync with Firestore
        await SubscriptionRepository.updateSubscription(userId, {
          subscriptionTier: subscriptionInfo.tier,
          subscriptionExpiresAt: subscriptionInfo.expiresAt,
          lastRevenueCatSync: Math.floor(Date.now() / 1000),
          revenueCatCustomerId: subscriptionInfo.customerId,
        });

        console.log('[SubscriptionContext] Successfully restored:', subscriptionInfo.tier);
        
        // Log successful restoration with full details
        await loggingService.log(purchasesRestored(true, trigger, {
          tier: subscriptionInfo.tier,
          expiresAt: subscriptionInfo.expiresAt,
          isLifetime: subscriptionInfo.expiresAt === null && subscriptionInfo.tier !== 'free',
          willRenew: subscriptionInfo.willRenew,
          source: subscriptionInfo.source,
          customerId: subscriptionInfo.customerId,
          entitlements,
        }));
        
        return true;
      }

      // No data returned - log as failed
      await loggingService.log(purchasesRestored(false, trigger, {
        errorMessage: 'No data returned',
      }));
      
      return false;
    } catch (error) {
      console.error('[SubscriptionContext] Restore error:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Log failed restoration
      await loggingService.log(purchasesRestored(false, trigger, {
        errorMessage,
      }));
      
      errorReporting.reportError(error, {
        screen: 'SubscriptionContext',
        action: 'restorePurchases',
      });
      return false;
    }
  };

  // Derived access checks
  const hasStandardAccess = tier === 'standard_premium' || tier === 'ai_premium';
  // Check for lifetime access (expiresAt === null means lifetime for paid tiers)
  const isLifetimeSubscription = (tier === 'ai_premium' || tier === 'standard_premium') && expiresAt === null;
  // Grant AI access based on tier only
  const hasAIAccess = tier === 'ai_premium';

  /**
   * Initialize subscription state and listen for auth changes
   */
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const setupAuthListener = async () => {
      // Subscribe to auth state changes
      unsubscribe = onAuthStateChanged(auth, async (user: any) => {
        console.log('[SubscriptionContext] 👤 Auth state changed:', user ? `User ${user.uid}` : 'Logged out');
        
        if (!user) {
          // User logged out
          if (Platform.OS !== 'web') {
            await SubscriptionService.logout();
          }
          setTier('free');
          setExpiresAt(null);
          setSource(undefined);
          setManagementURL(undefined);
          setIsLoading(false);
          return;
        }

        const userId = user.uid;
        setIsLoading(true);

        // On web: Load from Firestore only (synced from mobile)
        if ((Platform.OS as any) === 'web') {
          await loadSubscriptionState(userId);
          return;
        }

        // On native: Use RevenueCat directly (fast in-memory cache)
        console.log('[SubscriptionContext] 🚀 Loading from RevenueCat (fast path)');
        
        // Initialize RevenueCat
        await SubscriptionService.initialize();
        
        // Login to RevenueCat - this returns cached customer info if available
        // We don't pass userId explicitly - service uses auth.currentUser as source of truth
        const loginResult = await SubscriptionService.loginUser();
        
        if (loginResult.success && loginResult.data) {
          // Parse subscription info immediately from RevenueCat
          const subscriptionInfo = SubscriptionService.getSubscriptionInfo(loginResult.data);
          
          // Update state immediately - this makes the UI responsive
          setTier(subscriptionInfo.tier);
          setExpiresAt(subscriptionInfo.expiresAt);
          setSource(subscriptionInfo.source);
          setManagementURL(subscriptionInfo.managementURL);
          setIsLoading(false);
          
          console.log('[SubscriptionContext] ✅ Fast load complete:', subscriptionInfo.tier);
          
          // Sync to Firestore in background (non-blocking)
          SubscriptionRepository.updateSubscription(userId, {
            subscriptionTier: subscriptionInfo.tier,
            subscriptionExpiresAt: subscriptionInfo.expiresAt,
            lastRevenueCatSync: Math.floor(Date.now() / 1000),
            revenueCatCustomerId: subscriptionInfo.customerId,
          }).catch(err => {
            console.warn('[SubscriptionContext] Background Firestore sync failed:', err);
          });
          
          // Restore purchases in background to sync with new Firebase ID (non-blocking)
          // This is critical when user logs in with a different account
          restorePurchasesInternal('automatic').catch(err => {
            console.warn('[SubscriptionContext] Background purchase restore failed:', err);
          });
        } else {
          console.warn('[SubscriptionContext] Login failed, falling back to Firestore');
          await loadSubscriptionState(userId);
        }
      });
    };

    setupAuthListener();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  /**
   * Load subscription state from Firestore cache, then sync with RevenueCat
   */
  const loadSubscriptionState = async (userId: string) => {
    try {
      // First, load cached data from Firestore (fast)
      const cachedResult = await SubscriptionRepository.getSubscription(userId);
      if (cachedResult.success && cachedResult.data) {
        setTier(cachedResult.data.subscriptionTier);
        setExpiresAt(cachedResult.data.subscriptionExpiresAt);
        // Note: source and managementURL are not cached in Firestore, only fetched from RevenueCat
        
        console.log('[SubscriptionContext] Loaded from Firestore:', {
          tier: cachedResult.data.subscriptionTier,
          expiresAt: cachedResult.data.subscriptionExpiresAt,
          isLifetime: cachedResult.data.subscriptionExpiresAt === null && cachedResult.data.subscriptionTier !== 'free',
        });

        // On web, use Firestore data as source of truth (synced from mobile)
        if ((Platform.OS as any) === 'web') {
          setIsLoading(false);
          return; // Web relies on Firestore data synced from mobile purchases
        }

        // On native: Check if cache is stale
        const isStale = SubscriptionRepository.isSubscriptionStale(
          cachedResult.data.lastRevenueCatSync,
          5 // 5 minutes
        );

        if (!isStale) {
          setIsLoading(false);
          return; // Cache is fresh, no need to sync
        }
      } else if ((Platform.OS as any) === 'web') {
        // No subscription data in Firestore for web user
        console.log('[SubscriptionContext] No subscription data found in Firestore for web user');
        setIsLoading(false);
        return;
      }

      // Cache miss or stale - sync with RevenueCat (only on native)
      if ((Platform.OS as any) !== 'web') {
        await syncWithRevenueCat(userId);
      }

      setIsLoading(false);
    } catch (error) {
      console.error('[SubscriptionContext] Load state error:', error);
      errorReporting.reportError(error, {
        screen: 'SubscriptionContext',
        action: 'loadSubscriptionState',
        userId,
      });
      setIsLoading(false);
    }
  };

  /**
   * Sync subscription state with RevenueCat
   */
  const syncWithRevenueCat = async (userId: string) => {
    try {
      if ((Platform.OS as any) === 'web') {
        return; // Skip on web
      }

      const result = await SubscriptionService.getCustomerInfo(true);
      if (!result.success || !result.data) {
        console.warn('[SubscriptionContext] Failed to get customer info:', result.error);
        return;
      }

      const subscriptionInfo = SubscriptionService.getSubscriptionInfo(result.data);
      
      // Update local state
      setTier(subscriptionInfo.tier);
      setExpiresAt(subscriptionInfo.expiresAt);
      setSource(subscriptionInfo.source);
      setManagementURL(subscriptionInfo.managementURL);

      // Sync with Firestore (note: we don't persist source/managementURL, only tier/expiry)
      await SubscriptionRepository.updateSubscription(userId, {
        subscriptionTier: subscriptionInfo.tier,
        subscriptionExpiresAt: subscriptionInfo.expiresAt,
        lastRevenueCatSync: Math.floor(Date.now() / 1000),
        revenueCatCustomerId: subscriptionInfo.customerId,
      });

      console.log('[SubscriptionContext] Synced with RevenueCat:', subscriptionInfo.tier, 'source:', subscriptionInfo.source);
    } catch (error) {
      console.error('[SubscriptionContext] Sync error:', error);
      errorReporting.reportError(error, {
        screen: 'SubscriptionContext',
        action: 'syncWithRevenueCat',
        userId,
      });
    }
  };

  /**
   * Refresh subscription state (force sync with RevenueCat)
   */
  const refreshSubscription = useCallback(async () => {
    const userId = auth.currentUser?.uid;
    if (!userId) return;

    setIsLoading(true);
    await syncWithRevenueCat(userId);
    setIsLoading(false);
  }, []);

  /**
   * Purchase a package
   */
  const purchasePackage = useCallback(async (pkg: PurchasesPackage): Promise<'success' | 'cancelled' | 'failed'> => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Make the purchase
      const result = await SubscriptionService.purchasePackage(pkg);
      if (!result.success) {
        if (result.error === 'Purchase cancelled') {
          return 'cancelled';
        }
        return 'failed';
      }

      // Update state
      if (result.data) {
        const subscriptionInfo = SubscriptionService.getSubscriptionInfo(result.data);
        setTier(subscriptionInfo.tier);
        setExpiresAt(subscriptionInfo.expiresAt);
        setSource(subscriptionInfo.source);
        setManagementURL(subscriptionInfo.managementURL);

        // Sync with Firestore
        await SubscriptionRepository.updateSubscription(userId, {
          subscriptionTier: subscriptionInfo.tier,
          subscriptionExpiresAt: subscriptionInfo.expiresAt,
          lastRevenueCatSync: Math.floor(Date.now() / 1000),
          revenueCatCustomerId: subscriptionInfo.customerId,
        });
      }

      return 'success';
    } catch (error) {
      console.error('[SubscriptionContext] Purchase error:', error);
      errorReporting.reportError(error, {
        screen: 'SubscriptionContext',
        action: 'purchasePackage',
        metadata: { packageIdentifier: pkg.identifier },
      });
      return 'failed';
    }
  }, []);

  /**
   * Restore purchases (public API - wraps internal implementation)
   */
  const restorePurchases = useCallback(async (trigger: 'manual' | 'automatic' = 'manual'): Promise<boolean> => {
    return restorePurchasesInternal(trigger);
  }, []);

  /**
   * Show paywall (navigation will be handled by consuming component)
   */
  const showPaywall = useCallback(() => {
    if (paywallCallback) {
      paywallCallback();
    }
  }, [paywallCallback]);

  const value: SubscriptionContextValue = {
    tier,
    expiresAt,
    isLoading,
    source,
    managementURL,
    hasStandardAccess,
    hasAIAccess,
    refreshSubscription,
    purchasePackage,
    restorePurchases,
    showPaywall,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

/**
 * Hook to use subscription context
 */
export const useSubscriptionContext = (): SubscriptionContextValue => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscriptionContext must be used within SubscriptionProvider');
  }
  return context;
};

